import {
  ACCESS_LEVELS,
  accessRank,
  type AccessLevel,
  type Hardening,
} from '@badger-sentinel/rules';
import type { CellViewWire } from '@badger-sentinel/protocol';

import { compareAliases } from './alias.js';
// `model/sidc.ts`'s table, read for the *device* class rather than for a symbol:
// `deviceOf()` in the vendored file switches on these same keys, and reading them
// from one table is what stops a TT&C station being a `c2` unit in the rail and an
// `endpoint` on the board.
import { iconFor as iconOf } from './sidc.js';
import type { ScenarioUnit } from './scenario.js';
import type { CyberContactViewWire, CyberNetworkViewWire } from './view-types.js';

/**
 * The cyber picture, in `cyber-map.js`'s own vocabulary — Phase 7's half of the
 * facade, and the strip that carries what a card cannot.
 *
 * The renderer reads eleven members off `./scenario.js` that no other board
 * wants, and every one of them is built here from `CellViewWire`. Its model of
 * the world is three layers deep and ours is two, so the mapping is worth
 * stating before the code:
 *
 * | The renderer draws | Which for us is |
 * | --- | --- |
 * | a **gateway** per side (`NETWORKS`, keyed `BLU`/`OPF`) | the *team*'s whole estate |
 * | a **segment** per router (`ROUTERS`) | one `cyber.networks[]` row |
 * | **hosts** hanging off a router (`hostsOf`) | the assets whose `network` is that row |
 * | a **team** docked to a router (`isCyberTeam`) | a `friendly[]` asset in the CYBER segment |
 *
 * That is M5 F4 exactly: *the repository's `network` **is** the design's router,
 * and the "network" the design drew around several of them is the team.* No
 * rules-model change, and the topology falls out of a field every asset already
 * carries.
 *
 * ## Three things this module refuses to do
 *
 * **It draws no machine the wire did not put in `cyber.contacts`.** The enemy
 * half of the board is `view.cyber.contacts[]` and nothing else — not
 * `view.contacts[]`, which is the *detection* picture and a different question.
 * RB §5.6.2's parenthesis is load-bearing — a foothold makes an asset "visible
 * (to cyber; not FOUND)" — so the two lists stay apart in both directions: a
 * cyber-revealed host is not a board contact, and these records are deliberately
 * **not** added to `ScenarioModel.UNITS`, which is what `region-map.js` and
 * `orbital-map.js` draw from. That is the whole of must-not #1, and it is
 * structural rather than a filter somebody has to remember.
 *
 * **It states a network's posture once.** `hardening` is on the wire three times
 * — `friendly[].hardening`, `cyber.friendlyHardening[]` and
 * `cyber.networks[].hardening` — and the board reads the second and third. See
 * `hardeningOf` for why the first is not the same claim.
 *
 * **It never renders an unknown as the good state.** Two fields are nullable on
 * this wire and both are *earned* rather than missing: a network's `router` is
 * `null` until the machine is one of the viewer's cyber contacts, and `hardening`
 * is `null` until Reconnoiter reveals it (RB §5.6.1, §5.6.3). Access on one's own
 * kit is not on the wire at all. All three come out of here as an explicit
 * unknown — `ROUTER_UNKNOWN`, `HARDENING_UNKNOWN`, `ACCESS_UNKNOWN` — and the
 * vendored renderer draws each of them differently from every real value.
 */

// ---------------------------------------------------------------------------
// The three unknowns, named
// ---------------------------------------------------------------------------

/**
 * Access this seat has not been told about — a **fifth** value beside
 * `ACCESS_LEVELS`'s four, and the renderer already knows it: `accColor` tests
 * `acc === 'UNKNOWN'` for the muted ink and `accMark` draws `spec.text` as an
 * empty pip frame with a glyph beside it.
 *
 * It is the answer for **every one of the viewer's own assets**, always. The
 * meter on a friendly card asks *"which rungs does the adversary hold here"*,
 * and the projection does not answer it: RB §6.2 says a Collect teaches the
 * defender "nothing at all unless a Hunt succeeds", `CyberView` has no field for
 * it, and a board that drew NO_ACCESS instead would be telling fourteen seats
 * their estate is clean on no evidence at all.
 */
export const ACCESS_UNKNOWN = 'UNKNOWN';

/** A posture nobody has assessed. Edit 2's `unknown` footing form draws it. */
export const HARDENING_UNKNOWN = 'UNASSESSED';

/** A condition nobody has assessed. Edit 3's `ledOf` draws no lamp for it. */
export const CONDITION_UNKNOWN = 'UNASSESSED';

/** An enclave whose way in this seat cannot name. Drawn in the strip, never as an id. */
export const ROUTER_UNKNOWN = 'ROUTER UNKNOWN';

export type CyberAccess = AccessLevel | typeof ACCESS_UNKNOWN;
export type CyberHardening = Hardening | typeof HARDENING_UNKNOWN;

// ---------------------------------------------------------------------------
// The renderer's two constant tables
// ---------------------------------------------------------------------------

/**
 * Rungs on the ladder, and therefore pips in the meter: `FOOTHOLD · USER ·
 * ADMIN`. `NO_ACCESS` is the floor and draws nothing.
 */
export const ACCESS_PIPS = ACCESS_LEVELS.length - 1;

/** `S.ACCESS_SHORT[rung]` — the segment header's word, indexed by `accessRank`. */
export const ACCESS_SHORT: readonly string[] = Object.freeze([
  'NO ACCESS',
  'FOOTHOLD',
  'USER',
  'ADMIN',
]);

export interface AccessMark {
  readonly pips: number;
  readonly tone: 'warn' | 'red' | 'muted';
  /** Drawn beside an empty meter instead of filling it. Unknown only. */
  readonly text?: string;
  readonly label?: string;
}

/**
 * The meter over each card, and the legend's first group.
 *
 * `null` is a supported entry and means *no mark at all* — the renderer's
 * `accMark` returns early and its legend keys the row `NO MARK · NO_ACCESS`.
 * That is right for the floor: a card with no rungs held should not carry a
 * meter of three empty boxes competing with the ones that do.
 *
 * The **tone is not the ink**. `accMark` recolours a rung the viewer holds on an
 * adversary machine to BLUFOR blue, because that is our progress; amber and red
 * are reserved for rungs the adversary holds on us, so the alarm palette only
 * ever means bad news. What this table decides is which of the two alarm inks a
 * hostile rung uses — a foothold is not a breach.
 *
 * Key order is the legend's row order, so the floor reads first and the unknown
 * last.
 */
export const ACCESS_MARK: Readonly<Record<CyberAccess, AccessMark | null>> = Object.freeze({
  NO_ACCESS: null,
  FOOTHOLD: { pips: 1, tone: 'warn', label: 'FOOTHOLD' },
  USER: { pips: 2, tone: 'red', label: 'USER' },
  ADMIN: { pips: 3, tone: 'red', label: 'ADMIN' },
  /**
   * **No mark either, and for a stronger reason than the floor's.**
   *
   * The renderer supports an unknown meter — an empty pip frame with a glyph
   * beside it — and drawing it here was the first answer. Seen on a real board
   * it is fourteen identical `?` marks, one over every asset the seat owns, in
   * every game, forever: the meter on a friendly card asks *"which rungs does
   * the adversary hold here"* and the projection **structurally never answers
   * it** (RB §6.2, and `CyberView` has no field for it). That is not a value
   * that happens to be missing, it is a channel that carries nothing on this
   * side — and a mark that cannot distinguish anything is a control that does
   * nothing, which is the one thing this console keeps refusing to draw.
   *
   * So the meter appears only where it means something, which is the adversary
   * side, where `cyber.contacts[].access` is always one of the four real rungs
   * and never this. The two therefore never collide on one card.
   *
   * **The fact is still stated, twice, in words.** `drawSegment` prints
   * `UNASSESSED · 12 HOSTS` in the enclave's own title bar and `accColor` inks
   * the frame muted; the strip and the selection rail say `ACCESS UNASSESSED`.
   * That is the console's usual trade and Phase 5's finding 5 in a fourth place:
   * *a sentence that is right beats a mark that is wrong* — and here, a mark
   * that is right but says the same thing fourteen times.
   */
  UNKNOWN: null,
});

export interface Footing {
  readonly form: 'broken' | 'plain' | 'doubled' | 'beaded' | 'unknown';
  readonly label?: string;
}

/**
 * The tick under each icon, and the legend's second group.
 *
 * Four forms are the prototype's and the fifth is vendored edit 2's. They are
 * *statements*, which is why an unknown could not borrow one: `plain` means
 * NORMAL, and NORMAL is a rating somebody assessed.
 */
export const FOOTING: Readonly<Record<CyberHardening, Footing>> = Object.freeze({
  VULNERABLE: { form: 'broken', label: 'VULNERABLE' },
  NORMAL: { form: 'plain', label: 'NORMAL' },
  HARDENED: { form: 'doubled', label: 'HARDENED' },
  MONITORED: { form: 'beaded', label: 'MONITORED' },
  UNASSESSED: { form: 'unknown', label: 'UNASSESSED · RECONNOITER REVEALS IT' },
});

export function footingFor(hardening: string): Footing {
  return FOOTING[hardening as CyberHardening] ?? FOOTING.UNASSESSED;
}

/** `accessRank`, widened to carry the fifth value. Unknown is not rung zero. */
export function accessRung(access: string): number {
  if (access === ACCESS_UNKNOWN) return 0;
  const rank = accessRank(access as AccessLevel);
  return rank < 0 ? 0 : rank;
}

// ---------------------------------------------------------------------------
// The model
// ---------------------------------------------------------------------------

/** One segment's head, in `cyber-map.js`'s `ROUTERS` shape. */
export interface CyberRouter {
  /**
   * The key the renderer uses for **everything**: the card's uid, its caption,
   * the `bs-select` detail and the `bs-region` detail. It is the router
   * machine's id or alias when the wire names one, and the *network* id when it
   * does not — see `routerKeyOf`.
   */
  readonly id: string;
  readonly net: 'BLU' | 'OPF';
  /** The enclave's name. Drawn in the frame's title bar, never as the caption. */
  readonly name: string;
  readonly harden: CyberHardening;
  readonly status: string;
  /** Our network id — what the wrapper translates `bs-region` back into. */
  readonly network: string;
  /** False when `id` is standing in for a machine the wire would not name. */
  readonly named: boolean;
}

export interface CyberGateway {
  /** `'BLU'` or `'OPF'` — the renderer's `net`, matched against `ROUTERS[].net`. */
  readonly id: 'BLU' | 'OPF';
  readonly name: string;
}

export interface CyberModel {
  readonly ROUTERS: readonly CyberRouter[];
  readonly NETWORKS: readonly CyberGateway[];
  hostsOf(routerId: string): readonly ScenarioUnit[];
  routerIdOf(unitId: string): string;
  access(id: string): CyberAccess;
  hardenOf(id: string): CyberHardening;
  /** Our network id for a router key, or `null`. The `bs-region` translation. */
  networkOf(routerId: string): string | null;
}

/**
 * The segment key, and the one place the renderer's conflation is absorbed.
 *
 * `<cyber-map>` keys a segment on `router.id` and then spends that one string
 * four ways — card uid, card caption, `bs-select` detail, `bs-region` detail.
 * Our wire has two things it could be: `networks[].router` names the machine,
 * and it is `null` for an enclave the viewer has reached without reaching its
 * way in. A null key is not an option (the card would have no uid and two such
 * enclaves would collide), so the network id stands in — unique, stable, and
 * already what the rest of the picture calls the enclave.
 *
 * `named` is how everything downstream tells the two apart: the strip says
 * `ROUTER UNKNOWN` rather than printing the stand-in, and a click on an unnamed
 * router card selects the *enclave*, which is the only thing there is to select.
 */
function routerKeyOf(network: CyberNetworkViewWire): string {
  return network.router ?? network.id;
}

/**
 * A cyber-revealed machine, in the renderer's unit shape.
 *
 * Four of the five fields are deliberately thin, because the wire is: cyber
 * revealed *a machine on an enclave*, not what it is or how it is doing.
 *
 *  - `icon` is `frame`, which `deviceOf()` classifies as `endpoint` — the set's
 *    plainest shape, and the honest one for a host nobody has looked at.
 *  - `status` is `UNASSESSED`, which vendored edit 3 draws as **no lamp**. There
 *    is no status field on `CyberContactView` and the renderer's own third
 *    branch is a green one.
 *  - `name` is the enclave, because that is the only thing the wire says about
 *    it, and it is what the *builder's* target row says too.
 *  - `region` and `regime` are absent, and must stay absent: they are what the
 *    other two canvases place a marker by.
 */
function cyberHostUnit(contact: CyberContactViewWire): ScenarioUnit {
  return {
    id: contact.alias,
    side: 'OPF',
    icon: 'frame',
    // Never drawn on this canvas — it draws device art, not milsymbol — but the
    // shape is `ScenarioUnit` and a lie in an unread field is still a lie.
    sidc: '',
    name: contact.network,
    status: CONDITION_UNKNOWN,
  };
}

/**
 * Which hardening channel the board reads, decided from the projection.
 *
 * The wire carries a posture three times and they are **not** the same claim:
 *
 *  - `friendly[].hardening` is `asset.hardening` sent unconditionally — the
 *    world's truth about the machine;
 *  - `cyber.friendlyHardening[]` is `knowledge.knownHardening` filtered to own
 *    assets — *"own assets whose hardening this team knows"*, in the
 *    projection's own words;
 *  - `cyber.networks[].hardening` is the same knowledge map read for the
 *    router, and withheld entirely with the router it belongs to.
 *
 * In every fixture all three agree, which is exactly why the fixture cannot
 * decide this. The projection can: a scenario may author
 * `ownerKnowsHardening: false` on any asset (`model/scenario.ts`,
 * "not knowing your own kit is the exception worth authoring"), and
 * `SET_HARDENING` mutates the asset without touching the knowledge map. So
 * `friendlyHardening` **can** be shorter than `friendly[]`, and where the two
 * differ the short one is what this team has earned.
 *
 * The board reads `friendlyHardening` and `networks[].hardening`. An asset
 * missing from the first is `UNASSESSED`, not NORMAL and not
 * `friendly[].hardening` — a defensive board that quietly showed the truth
 * would be teaching the seat a posture RB §5.6.1 says they may not have.
 */
function hardeningOf(view: CellViewWire): ReadonlyMap<string, CyberHardening> {
  const known = new Map<string, CyberHardening>();
  for (const row of view.cyber.friendlyHardening) known.set(row.asset, row.hardening);
  for (const contact of view.cyber.contacts) {
    if (contact.hardening !== null) known.set(contact.alias, contact.hardening);
  }
  return known;
}

export function cyberModel(view: CellViewWire): CyberModel {
  const viewerTeam = view.viewer.team;
  const sideOf = (network: CyberNetworkViewWire): 'BLU' | 'OPF' =>
    network.side === viewerTeam ? 'BLU' : 'OPF';

  const ROUTERS: readonly CyberRouter[] = view.cyber.networks.map((network) => ({
    id: routerKeyOf(network),
    net: sideOf(network),
    name: network.name,
    harden: network.hardening ?? HARDENING_UNKNOWN,
    // A network is not a machine and has no condition. `drawSegment` reads this
    // only through `ledOf`, which edit 3 answers with no lamp — the router card
    // stands for an enclave whose own condition the wire never states.
    status: CONDITION_UNKNOWN,
    network: network.id,
    named: network.router !== null,
  }));

  const routerByNetwork = new Map(ROUTERS.map((router) => [router.network, router]));
  const networkByRouter = new Map(ROUTERS.map((router) => [router.id, router.network]));
  const hardening = hardeningOf(view);

  // The access the *viewer* holds, keyed by alias. Own assets are absent from
  // this map on purpose and fall through to `ACCESS_UNKNOWN` — see its header.
  const access = new Map<string, AccessLevel>(
    view.cyber.contacts.map((contact) => [contact.alias, contact.access]),
  );

  /**
   * Everything on one enclave.
   *
   * Friendly assets come from `friendly[]` by `network`; enemy machines come
   * from `cyber.contacts[]` by the same field. The **router machine itself is
   * dropped when it is named**, because `drawSegment` draws it as the segment's
   * own card — leaving it in would put one machine on the board twice, once as
   * the way in and once as a thing behind it.
   *
   * Contacts are ordered by `hash(alias)` and assets by id, the split
   * `model/roster.ts` already makes: an alias order that followed the wire would
   * be a channel (must-not #5), and an own id is a real name of the viewer's own.
   * The renderer lays the grid out in the order it is handed.
   */
  const hostsOf = (routerId: string): readonly ScenarioUnit[] => {
    const networkId = networkByRouter.get(routerId);
    if (networkId === undefined) return [];
    const router = routerByNetwork.get(networkId);
    const drop = router !== undefined && router.named ? router.id : null;

    if (router?.net === 'BLU') {
      return view.friendly
        .filter((asset) => asset.network === networkId && asset.id !== drop)
        .map(assetHostUnit)
        .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
    }
    return view.cyber.contacts
      .filter((contact) => contact.network === networkId && contact.alias !== drop)
      .map(cyberHostUnit)
      .sort((a, b) => compareAliases(a.id, b.id));
  };

  const routerIdOf = (unitId: string): string => {
    const asset = view.friendly.find((candidate) => candidate.id === unitId);
    if (asset !== undefined) return routerByNetwork.get(asset.network)?.id ?? '';
    const contact = view.cyber.contacts.find((candidate) => candidate.alias === unitId);
    if (contact !== undefined) return routerByNetwork.get(contact.network)?.id ?? '';
    return '';
  };

  return {
    ROUTERS,
    NETWORKS: gatewaysFor(view),
    hostsOf,
    routerIdOf,
    access: (id) => access.get(id) ?? ACCESS_UNKNOWN,
    hardenOf: (id) => hardening.get(id) ?? HARDENING_UNKNOWN,
    networkOf: (routerId) => networkByRouter.get(routerId) ?? null,
  };
}

/**
 * A friendly asset as a host on its enclave.
 *
 * Built here rather than reused from `assetUnit` because the two canvases want
 * different things from the same asset: the theatre map wants a region and a
 * SIDC, and this one wants a device class and a condition. In particular
 * `regime` is **not** carried across — `deviceOf()` reads it as "this thing is
 * in orbit, draw it as a satellite link", which is true, and `region` is dropped
 * because nothing here places anything on the ground.
 */
function assetHostUnit(asset: CellViewWire['friendly'][number]): ScenarioUnit {
  const segment =
    asset.segment === 'SPACE' ? 'SPACE' : asset.segment === 'CYBER' ? 'CYBER' : 'GROUND';
  return {
    id: asset.id,
    side: 'BLU',
    ...(asset.location.kind === 'ORBIT' ? { regime: asset.location.band } : {}),
    icon: iconOf(asset.typeId, segment),
    sidc: '',
    name: asset.typeName,
    status: asset.status,
    ...(asset.segment === 'CYBER' ? { cyber: true } : {}),
  };
}

/**
 * The two gateways, named viewer-relatively.
 *
 * `NETWORKS` is keyed `BLU`/`OPF` because the renderer looks it up with the
 * segment's `net`, and the words are the rails' own (`ROSTER_GROUP_LABELS`) so
 * one estate is not `FRIENDLY` in the left rail and `BLUFOR` on the canvas.
 * Both are always drawn: a gateway with no segments under it is the honest
 * picture of an adversary estate this seat has never reached, and the
 * transit lane between them is what the whole canvas is for.
 */
function gatewaysFor(view: CellViewWire): readonly CyberGateway[] {
  void view;
  return Object.freeze([
    { id: 'BLU' as const, name: 'FRIENDLY NET' },
    { id: 'OPF' as const, name: 'ADVERSARY NET' },
  ]);
}

// ---------------------------------------------------------------------------
// The strip under the canvas
// ---------------------------------------------------------------------------

/**
 * One row per enclave, plus the two lists that hang off no enclave at all.
 *
 * The same argument as Phase 6's band strip: *a sentence that is right beats a
 * mark that is wrong.* Four facts about this picture have no channel on the
 * canvas — a router the wire would not name, a posture nobody assessed, the
 * assets this team has cut off, and the footholds it holds — and three of them
 * are the phase's must-nots. They are stated here in words, once each.
 */
export interface CyberNetworkRow {
  readonly id: string;
  readonly name: string;
  readonly net: 'BLU' | 'OPF';
  /** The machine, or `ROUTER_UNKNOWN`. Never the stand-in key. */
  readonly router: string;
  readonly routerNamed: boolean;
  readonly hardening: CyberHardening;
  readonly access: CyberAccess;
  /**
   * **Exactly what the canvas's own segment header counts**, which is machines
   * hanging off the router: not the router, which is drawn *as* the segment, and
   * not a docked cyber team, which is an actor rather than a host.
   *
   * Computed the renderer's way rather than as "assets on this enclave" for one
   * reason: the header prints `12 HOSTS` a few pixels above this row, and two
   * numbers for one word a foot apart is the thing an operator stops trusting
   * first. The strip does not draw it at all — the canvas already says it — but
   * the selection rail does, and that is the surface where it has to agree.
   */
  readonly hosts: number;
  /** Own assets on this enclave this team has isolated (RB §5.6.5). */
  readonly isolated: readonly string[];
  /** True when this enclave is one of `cyber.footholds`. */
  readonly foothold: boolean;
}

export function networkRows(view: CellViewWire): readonly CyberNetworkRow[] {
  const model = cyberModel(view);
  const footholds = new Set(view.cyber.footholds);
  const isolated = new Set(view.cyber.friendlyIsolated);
  const networkOfAsset = new Map(view.friendly.map((asset) => [asset.id, asset.network]));

  return model.ROUTERS.map((router) => ({
    id: router.network,
    name: router.name,
    net: router.net,
    router: router.named ? router.id : ROUTER_UNKNOWN,
    routerNamed: router.named,
    hardening: router.harden,
    // The enclave's own rung is the rung on its way in. An enclave whose router
    // this seat cannot name has no rung to read, which is `UNKNOWN` and not zero.
    access: router.named ? model.access(router.id) : ACCESS_UNKNOWN,
    hosts: model.hostsOf(router.id).filter((host) => host.cyber !== true).length,
    isolated: [...isolated]
      .filter((id) => networkOfAsset.get(id) === router.network)
      .sort((a, b) => (a < b ? -1 : 1)),
    foothold: footholds.has(router.network),
  }));
}
